import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TemplateField,
  createTemplateField,
  updateTemplateField,
  deleteTemplateField
} from '@/services/supabase/templates';
import { templateKeys } from './useTemplateQueries';

// Create template field mutation
export function useCreateTemplateFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (field: {
      template_id: number | string;
      section_id?: number | string | null;
      field_name: string;
      field_type: 'text' | 'textarea' | 'number' | 'dropdown' | 'checkbox' | 'date';
      is_required?: boolean;
      default_value?: string | null;
      options?: string | string[] | null;
      validation_rules?: any | null;
      order_position?: number;
    }) => createTemplateField(field),
    onSuccess: async (_, variables) => {
      // Ensure template_id is a number for consistent cache keys
      const templateId = Number(variables.template_id);

      // Force refetch template with fields query for immediate updates
      await queryClient.refetchQueries({
        queryKey: [...templateKeys.detail(templateId), 'fields']
      });

      // Also refetch template lists to update any template metadata
      await queryClient.refetchQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Update template field mutation
export function useUpdateTemplateFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fieldId,
      data,
      templateId
    }: {
      fieldId: number | string;
      data: Partial<TemplateField>;
      templateId: number | string;
    }) => updateTemplateField(fieldId, data, templateId),
    onSuccess: async (_, variables) => {
      // Ensure templateId is a number for consistent cache keys
      const templateId = Number(variables.templateId);

      // Force refetch template with fields query for immediate updates
      await queryClient.refetchQueries({
        queryKey: [...templateKeys.detail(templateId), 'fields']
      });

      // Also refetch template lists to update any template metadata
      await queryClient.refetchQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Delete template field mutation
export function useDeleteTemplateFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fieldId,
      templateId
    }: {
      fieldId: number | string;
      templateId: number | string;
    }) => deleteTemplateField(fieldId, templateId),
    onSuccess: async (_, variables) => {
      // Ensure templateId is a number for consistent cache keys
      const templateId = Number(variables.templateId);

      // Force refetch template with fields query for immediate updates
      await queryClient.refetchQueries({
        queryKey: [...templateKeys.detail(templateId), 'fields']
      });

      // Also refetch template lists to update any template metadata
      await queryClient.refetchQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}
